import { getParent, types, type Instance } from 'mobx-state-tree';
import ObjectInspector from './object_inspector';
import EventInspector from './event_inspector';
import DependencyInspector from './dependency_inspector';
import MethodInspector from './method_inspector';
import FSMInspector from './fsm_inspector';
import StateInspector from './state_inspector';
import TransitionInspector from './transition_inspector';

// The Inspector store's parent is the UI store, from which it only reaches
// `project.setBusy`. Narrow structural interface to avoid an import cycle with
// the UI store (which embeds Inspector) — the §3 hand-declared-boundary move.
interface IInspectorUIParent {
  readonly project: { setBusy(busy: boolean): void };
}

/**
 * UI state for the inspector view.
 *
 * Aggregates four stores for the different types of inspectors.
 */
const Inspector = types
  .model('Inspector', {
    objectInspector: types.optional(ObjectInspector, () => ObjectInspector.create({})),
    eventInspector: types.optional(EventInspector, () => EventInspector.create({})),
    dependencyInspector: types.optional(DependencyInspector, () => DependencyInspector.create({})),
    methodInspector: types.optional(MethodInspector, () => MethodInspector.create({})),
    fsmInspector: types.optional(FSMInspector, () => FSMInspector.create({})),
    stateInspector: types.optional(StateInspector, () => StateInspector.create({})),
    transitionInspector: types.optional(TransitionInspector, () => TransitionInspector.create({})),
  })
  .views((self) => ({
    // the inspector is dirty when any of the inspectors is dirty
    get dirty() {
      return (
        self.objectInspector.dirty ||
        self.eventInspector.dirty ||
        self.dependencyInspector.dirty ||
        self.methodInspector.dirty ||
        self.fsmInspector.dirty ||
        self.stateInspector.dirty ||
        self.transitionInspector.dirty
      );
    },
    get selectedMPC() {
      return self.objectInspector.selectedMPC;
    },
    get addedMPCs() {
      return self.objectInspector.addedMPCs;
    },
  }))
  .actions((self) => ({
    // discard changes in all the inspectors
    discardChanges() {
      self.objectInspector.discardChanges();
      self.eventInspector.discardChanges();
      self.dependencyInspector.discardChanges();
      self.methodInspector.discardChanges();
      self.fsmInspector.discardChanges();
      self.stateInspector.discardChanges();
      self.transitionInspector.discardChanges();
    },
    clearSelections() {
      self.objectInspector.clearSelections();
    },
    setProjectBusy(busy: boolean) {
      const project = getParent<IInspectorUIParent>(self).project;
      project.setBusy(busy);
    },
  }));

export type IInspector = Instance<typeof Inspector>;

export default Inspector;
